/**
 * Fetches similar apps. Lowest priority slice — feeds the competitors sidebar
 * and the lazy market-analysis chart components. Server background-generates
 * the embedding if missing instead of blocking the response.
 */

import { useHydratedSearchParams } from '@/hooks/use-hydrated-search-params';
import { useQuery } from '@tanstack/react-query';

export type AppSimilarPayload = {
  items: any[];
  embeddingReady: boolean;
};

export function useAppSimilar(appId: number, limit = 60) {
  const searchParams = useHydratedSearchParams();
  const country = searchParams.get('country') || '';

  const { data, isLoading, error } = useQuery<AppSimilarPayload, Error>({
    queryKey: ['app-similar', appId, country, limit],
    queryFn: async () => {
      const qs = new URLSearchParams({ appId: String(appId), limit: String(limit) });
      if (country) qs.set('country', country);
      const res = await fetch(`/api/app-detail/similar?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch similar apps');
      const payload = await res.json();
      return {
        items: Array.isArray(payload?.items) ? payload.items : [],
        embeddingReady: Boolean(payload?.embeddingReady),
      };
    },
    enabled: !!appId,
    staleTime: 10 * 60 * 1000,
    // If the embedding wasn't ready on first load, retry shortly so the
    // sidebar fills in once the background job finishes.
    refetchInterval: (q) => (q.state.data && !q.state.data.embeddingReady ? 8000 : false),
  });

  return {
    items: data?.items || [],
    embeddingReady: data?.embeddingReady ?? true,
    isLoading,
    error: error?.message || null,
  };
}
